import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Shield, Lock, Bell, Power, MapPin, AlertTriangle } from 'lucide-react';
import '../styles/Pages.css';
import { Link } from 'react-router-dom';

const Security = () => {
  const features = [
    {
      icon: <Shield size={24} />,
      title: 'Protección Antirrobo',
      description: 'Sistemas avanzados para prevenir y responder ante intentos de robo de vehículos.'
    },
    {
      icon: <Bell size={24} />,
      title: 'Alertas en Tiempo Real',
      description: 'Notificaciones instantáneas sobre eventos sospechosos o movimientos no autorizados.'
    },
    {
      icon: <Power size={24} />,
      title: 'Bloqueo Remoto de Motor',
      description: 'Capacidad para inmovilizar vehículos a distancia en caso de robo o uso no autorizado.'
    },
    {
      icon: <MapPin size={24} />,
      title: 'Geocercas de Seguridad',
      description: 'Definición de zonas permitidas con alertas automáticas cuando un vehículo sale del perímetro.'
    },
    {
      icon: <AlertTriangle size={24} />,
      title: 'Botón de Pánico',
      description: 'Sistema de alerta de emergencia para conductores en situaciones de peligro.'
    },
    {
      icon: <Lock size={24} />,
      title: 'Historial de Seguridad',
      description: 'Registro detallado de todos los eventos de seguridad para análisis y evidencia.'
    }
  ];

  return (
    <div className="page-container">
      <Header scrolled={true} />
      
      <main className="page-content">
        <section className="page-hero">
          <div className="container">
            <h1>Soluciones de Seguridad</h1>
            <p>Protección avanzada para tu flota vehicular y conductores</p>
          </div>
        </section>
        
        <section className="page-section">
          <div className="container">
            <div className="page-grid">
              <div className="page-text">
                <h2>Seguridad Integral para tu Flota</h2>
                <p>En un entorno donde los riesgos de robo y uso indebido de vehículos son cada vez mayores, VillaTracking ofrece soluciones de seguridad robustas diseñadas para proteger tus activos más valiosos.</p>
                <p>Nuestro sistema de seguridad vehicular combina tecnología GPS avanzada, alertas en tiempo real y funcionalidades de respuesta rápida para prevenir robos, recuperar vehículos sustraídos y garantizar la seguridad de tus conductores.</p>
                <Link to="/#contact" className="btn btn-primary btn-lg">Solicitar Demo</Link>
              </div>
              <div className="page-image">
                <img src="img/security1.webp" alt="Seguridad VillaTracking" />
              </div>
            </div>
          </div>
        </section>
        
        <section className="page-section page-section-alt">
          <div className="container">
            <h2 className="text-center">Características de Seguridad Avanzadas</h2>
            <div className="features-grid-3">
              {features.map((feature, index) => (
                <div className="feature-card" key={index}>
                  <div className="feature-icon">
                    {feature.icon}
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
        
        <section className="page-section">
          <div className="container">
            <div className="security-workflow">
              <h2 className="text-center">Cómo Funciona Nuestro Sistema de Seguridad</h2>
              
              <div className="security-scenario">
                <h3>Escenario: Intento de Robo</h3>
                <div className="scenario-steps">
                  <div className="scenario-step">
                    <div className="scenario-number">1</div>
                    <div className="scenario-content">
                      <h4>Detección</h4>
                      <p>El sistema detecta movimiento no autorizado del vehículo fuera de horario o sin llave autorizada.</p>
                    </div>
                  </div>
                  
                  <div className="scenario-step">
                    <div className="scenario-number">2</div>
                    <div className="scenario-content">
                      <h4>Alerta Inmediata</h4>
                      <p>Se envían notificaciones instantáneas a los administradores y contactos designados a través de múltiples canales.</p>
                    </div>
                  </div>
                  
                  <div className="scenario-step">
                    <div className="scenario-number">3</div>
                    <div className="scenario-content">
                      <h4>Seguimiento en Vivo</h4>
                      <p>La plataforma muestra la ubicación exacta y ruta del vehículo en tiempo real.</p>
                    </div>
                  </div>
                  
                  <div className="scenario-step">
                    <div className="scenario-number">4</div>
                    <div className="scenario-content">
                      <h4>Acción Remota</h4>
                      <p>Los administradores pueden activar el bloqueo remoto del motor para inmovilizar el vehículo de forma segura.</p>
                    </div>
                  </div>
                  
                  <div className="scenario-step">
                    <div className="scenario-number">5</div>
                    <div className="scenario-content">
                      <h4>Recuperación</h4>
                      <p>La información precisa de ubicación se comparte con las autoridades para una rápida recuperación.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        <section className="page-section page-section-alt">
          <div className="container">
            <div className="page-grid">
              <div className="page-image">
                <img src="img/security2.jpeg" alt="Centro de Control" />
              </div>
              <div className="page-text">
                <h2>Centro de Control de Seguridad</h2>
                <p>Nuestro Centro de Control de Seguridad proporciona una visión completa y en tiempo real del estado de seguridad de toda tu flota, permitiéndote responder rápidamente ante cualquier incidente.</p>
                <ul className="feature-list">
                  <li>Dashboard de seguridad con alertas priorizadas</li>
                  <li>Visualización de todos los vehículos en un mapa interactivo</li>
                  <li>Historial completo de eventos de seguridad</li>
                  <li>Controles de acción rápida para respuesta inmediata</li>
                  <li>Protocolos de seguridad personalizables</li>
                  <li>Integración con servicios de seguridad externos</li>
                </ul>
              </div>
            </div>
          </div>
        </section>
        
        <section className="page-section">
          <div className="container">
            <div className="testimonial-large">
              <div className="testimonial-content">
                <div className="testimonial-quote">
                  <p>"Después de implementar VillaTracking, recuperamos dos vehículos robados en menos de 24 horas. El sistema de alertas tempranas y la precisión del GPS fueron fundamentales para la rápida localización. La inversión se pagó por sí sola con solo este incidente."</p>
                </div>
                <div className="testimonial-author">
                  <div className="author-image">
                    <img src="https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" alt="Carlos Mendoza" />
                  </div>
                  <div className="author-info">
                    <h4>Carlos Mendoza</h4>
                    <p>Director de Operaciones, Transportes Rápidos S.A.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="security-stats">
              <div className="stat-item">
                <div className="stat-value">98%</div>
                <div className="stat-label">Tasa de recuperación de vehículos robados</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">85%</div>
                <div className="stat-label">Reducción en intentos de robo</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">5 min</div>
                <div className="stat-label">Tiempo promedio de respuesta ante alertas</div>
              </div>
            </div>
          </div>
        </section>
        
        <section className="page-cta">
          <div className="container">
            <h2>Protege tu flota con la mejor tecnología de seguridad</h2>
            <p>Implementa VillaTracking y duerme tranquilo sabiendo que tus vehículos están protegidos</p>
            <Link to="/#contact" className="btn btn-primary btn-lg">Solicitar Demo</Link>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default Security;